import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pprApi, usersApi, type PprJob } from '@/lib/apiClient';
import { fmtDate } from '../pprShared';
import { reconcileNotes } from '../notesSync';

/**
 * Notes & History tab.
 *
 * • Notes: free-text coordinator notes on the job. Saves via pprApi.updateJob.
 *   Dirty-tracked so the Save button only fires when there are real changes.
 *
 * • History: read-only audit. Uses the timestamps and user-id columns already
 *   on the ppr_jobs row (created_at/by, updated_at/by, financials_complete_date,
 *   president_consulted). No new backend, no audit-log table — full field-level
 *   audit was scoped out as post-v1.
 *
 * User UUIDs are resolved to display names via usersApi.list() (cached for
 * 5 min by React Query — likely already in cache from other modules).
 */
export default function NotesHistoryTab({
  job,
  canEdit,
}: {
  job: PprJob;
  canEdit: boolean;
}) {
  const qc = useQueryClient();

  const serverNotes = job.notes ?? '';
  const [notes, setNotes] = useState(serverNotes);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dirty = notes !== serverNotes;

  /**
   * Adopt notes that changed on the SERVER while this tab was mounted.
   *
   * Originally this existed because cancelling a review appended its reason to
   * `notes`; cancellation now writes its own columns (see PprService.cancel), so
   * the remaining case is two people — or one person in two tabs — editing the
   * same job's notes. Without this, the textarea keeps its pre-refetch value,
   * which then reads as an unsaved edit: the Save button lights up and pressing
   * it PATCHes the old text back over whatever arrived.
   *
   * Local edits win: if the coordinator has typed something we keep it and warn
   * below rather than discarding their work.
   *
   * Decision table lives in reconcileNotes() — see the note there on why our own
   * save must be distinguished from a concurrent edit first.
   */
  const lastServerRef = useRef(serverNotes);
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const [serverMovedUnderEdit, setServerMovedUnderEdit] = useState(false);
  useEffect(() => {
    const outcome = reconcileNotes(lastServerRef.current, serverNotes, notesRef.current);
    if (outcome.action === 'none') return;
    lastServerRef.current = serverNotes;
    if (outcome.action === 'adopt') {
      setNotes(outcome.value);
      setServerMovedUnderEdit(false);
    } else if (outcome.action === 'in-sync') {
      setServerMovedUnderEdit(false);
    } else {
      setServerMovedUnderEdit(true);
    }
  }, [serverNotes]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const submitted = notes.trim();
      await pprApi.updateJob(job.id, { notes: submitted || null });
      return submitted;
    },
    // Adopt the value we actually SENT. We save `notes.trim()`, so a textarea
    // with stray whitespace would otherwise stay permanently "dirty" against the
    // normalized value that comes back — Save stuck enabled after a successful
    // save, and the reconcile below seeing a change it can't attribute to us.
    onSuccess: (submitted) => {
      setSaveError(null);
      setNotes(submitted);
      qc.invalidateQueries({ queryKey: ['ppr', 'job', job.id] });
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  // For resolving createdById / updatedById → display name.
  const { data: users } = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => usersApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const nameOf = (uid: string | null): string => {
    if (!uid) return 'unknown';
    const u = users?.find((u) => u.id === uid);
    return u?.displayName || u?.email || uid;
  };

  return (
    <>
      {/* ─── Notes ─────────────────────────────────────────────── */}
      <div className="ppr-fin-section">
        <h3>Notes</h3>
        <textarea
          className="ppr-notes-textarea"
          disabled={!canEdit}
          placeholder={canEdit ? 'Add coordinator notes — context, follow-ups, anything not captured elsewhere…' : 'No notes set.'}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {serverMovedUnderEdit && (
          <p
            className="ppr-fin-error"
            style={{ marginTop: 'var(--ew-s-2)' }}
            role="status"
          >
            These notes were changed elsewhere while you were editing — someone else
            saved over them. Saving will overwrite their version. Copy your text,
            reload, and re-apply it.
          </p>
        )}
        {canEdit && (
          <div className="ppr-fin-actions" style={{ borderTop: 'none', paddingTop: 'var(--ew-s-2)' }}>
            <button
              className="btn btn-primary"
              disabled={!dirty || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? 'Saving…' : dirty ? 'Save notes' : 'No changes'}
            </button>
            {!dirty && !saveError && (job.updatedAt) && (
              <span className="ppr-fin-saved">Last saved {fmtDate(job.updatedAt)}</span>
            )}
            {saveError && <span className="ppr-fin-error">{saveError}</span>}
          </div>
        )}
      </div>

      {/* ─── History ───────────────────────────────────────────── */}
      <div className="ppr-fin-section">
        <h3>History</h3>
        <ul className="ppr-history-list">
          <li className="ppr-history-item">
            <span className="label">Created</span>
            by {nameOf(job.createdById)} on {fmtDate(job.createdAt)}
          </li>
          <li className="ppr-history-item">
            <span className="label">Last updated</span>
            by {nameOf(job.updatedById)} on {fmtDate(job.updatedAt)}
          </li>
          {job.financialsComplete && (
            <li className="ppr-history-item">
              <span className="label">Financials</span>
              marked complete on {fmtDate(job.financialsCompleteDate)}
            </li>
          )}
          {job.presidentConsulted && (
            <li className="ppr-history-item">
              <span className="label">President consulted</span>
              {job.presidentNotes ? <>: {job.presidentNotes}</> : ' (no notes recorded)'}
            </li>
          )}
          {job.broaderMeetingRequired && (
            <li className="ppr-history-item">
              <span className="label">Outcome</span>
              broader meeting required
            </li>
          )}
        </ul>
        <p style={{ marginTop: 'var(--ew-s-3)', color: 'var(--ew-n-500)', fontSize: 12, fontStyle: 'italic' }}>
          Full field-level audit history (every change tracked individually) is post-v1.
          What's shown here uses the row-level timestamps already on the PPR.
        </p>
      </div>
    </>
  );
}
